import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import clsx from 'clsx'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { MilestoneCheck } from './components/milestone-check/milestone-check'
import { MilestoneForm } from './components/milestone-form/milestone-form'
import { WorkspaceBody } from './components/workspace-body/workspace-body'
import { WorkspaceWorkMode } from './constants/workspace.constants'
import styles from './workspace.page.module.scss'
import { useModal } from '../../../common/hooks/formik/modal.hook'
import { ModalCenter } from '../../../common/ui/modal/modal-center'
import { ModalRight } from '../../../common/ui/modal/modal-right'
import { PureButton } from '../../../common/ui/pure-button/pure-button'
import { useGetObjects } from '../../../entities/objects/hooks/object.hooks'
import { MilestoneChecklistStatus } from '../../../entities/workflow/constants/workflow.constants'
import { useGetAllChecklists, useGetUserWorkflows } from '../../../entities/workflow/hooks/workflow.hooks'
import { useWorkspaceStore } from '../../../entities/workflow/stores/workspace.store'
import { ObjectTree } from '../../shared/components/object-tree/object-tree'

export const WorkspacePage = () => {
  const { t } = useTranslation()
  const { data: objects, isLoading: isObjectsLoading } = useGetObjects()
  const { data: milestones, isLoading: isMilestonesLoading } = useGetUserWorkflows()
  const { data: allChecklists, isLoading: isChecklistLoading } = useGetAllChecklists(objects ? objects.map((object) => object.id) : [])
  const [isMilestoneOpened, openMilestone, closeMilestone] = useModal()
  const [isFilterCollapsed, setIsFilterCollapsed] = useState(false)
  const milestoneModalFormRef = useRef<{ submit: (status: MilestoneChecklistStatus) => Promise<boolean> } | null>(null)
  const {
    workspaceConfiguration,
    checkedMilestones,
    selectedMilestone,
    selectedObject,
    isExpanded,
    checkedObjects,
    setSelectedMilestones,
    setSelectedMilestone,
    setSelectedObject,
    setExpanded,
    setCheckedObjects,
  } = useWorkspaceStore()

  const readyObjects = useMemo(() => {
    if (!checkedObjects.size) return objects

    return objects?.filter((obj) => checkedObjects.has(obj.id))
  }, [objects, checkedObjects])

  useEffect(() => {
    if (milestones && !checkedMilestones.size) {
      const milestonesSet = new Set([...milestones.map((milestone) => milestone.id)])
      setSelectedMilestones(milestonesSet)
    }
  }, [milestones])

  useEffect(() => {
    if (selectedMilestone) {
      openMilestone()
    } else {
      closeMilestone()
    }
  }, [selectedMilestone])

  const handleMilestoneClose = () => {
    setSelectedMilestone(null)
    setSelectedObject(null)
  }

  const handleMilestoneExpand = () => {
    setExpanded(!isExpanded)
  }

  const handleSaveChecklist = async (status: MilestoneChecklistStatus) => {
    const result = await milestoneModalFormRef.current?.submit(status)
    if (result) {
      handleMilestoneClose()
      closeMilestone()
    }
  }

  if (isObjectsLoading || isMilestonesLoading || isChecklistLoading) {
    return <div className="d-flex justify-content-center w-100">{t('common.loading')}...</div>
  }

  const handleObjectsCheck = (objectIds: number[]) => {
    setCheckedObjects(new Set(objectIds))
  }

  const handleCollapseClick = () => {
    setIsFilterCollapsed(!isFilterCollapsed)
  }

  const fillModalContent =
    !!selectedMilestone && !!selectedObject ? (
      <MilestoneForm
        ref={milestoneModalFormRef}
        handleSaveChecklist={handleSaveChecklist}
        isExpanded={isExpanded}
        milestone={selectedMilestone}
        object={selectedObject}
        onClose={handleMilestoneClose}
        onExpand={handleMilestoneExpand}
      />
    ) : (
      <div>{t('workspace.selectObjectAndMilestone')}</div>
    )

  const milestoneFillModal = isExpanded ? (
    <ModalCenter
      closable={false}
      footer={false}
      open={isMilestoneOpened}
      width="60%"
    >
      {fillModalContent}
    </ModalCenter>
  ) : (
    <ModalRight
      closable={false}
      footer={false}
      open={isMilestoneOpened}
    >
      {fillModalContent}
    </ModalRight>
  )

  return (
    <>
      <div className={clsx(styles.workspacesPage)}>
        <div
          className={clsx(
            {
              'col-3': !isFilterCollapsed,
              [styles.workspaceFilterCollapsed]: isFilterCollapsed,
            },
            'position-relative',
          )}
        >
          {objects && !isFilterCollapsed ? (
            <ObjectTree
              checked={checkedObjects}
              objects={objects}
              onCheck={handleObjectsCheck}
            />
          ) : null}
          <div
            className={clsx(styles.filterCollapse)}
            onClick={handleCollapseClick}
          >
            {isFilterCollapsed ? <RightOutlined /> : <LeftOutlined />}
          </div>
        </div>
        <div
          className={clsx({
            'col-9': !isFilterCollapsed,
            [styles.workspaceTableExpanded]: isFilterCollapsed,
          })}
        >
          {objects ? (
            <WorkspaceBody
              checklists={allChecklists}
              milestones={milestones}
              objects={readyObjects ?? []}
            />
          ) : null}
        </div>
      </div>
      {workspaceConfiguration.workMode === WorkspaceWorkMode.Checking ? (
        <ModalCenter
          footer={
            <div className="d-flex justify-content-between w-100">
              <div>
                <PureButton onClick={handleMilestoneClose}>{t('common.cancel')}</PureButton>
              </div>
              {selectedMilestone?.shouldBeChecked ? (
                <div>
                  <PureButton
                    className="warning-btn"
                    onClick={() => handleSaveChecklist(MilestoneChecklistStatus.CheckFailed)}
                    size="small"
                    type="primary"
                  >
                    {t('milestone.checkFailed')}
                  </PureButton>
                  <PureButton
                    className="ml-1 success-btn"
                    onClick={() => handleSaveChecklist(MilestoneChecklistStatus.Completed)}
                    size="small"
                    type="primary"
                  >
                    {t('milestone.complete')}
                  </PureButton>
                </div>
              ) : (
                <div className={clsx('success-text d-flex align-items-center')}>{t('milestone.noNeedCheck')}</div>
              )}
            </div>
          }
          height="90vh"
          okText={t('common.save')}
          onCancel={handleMilestoneClose}
          open={isMilestoneOpened}
          width="90wv"
          destroyOnClose
        >
          {!!selectedMilestone && !!selectedObject ? (
            <MilestoneCheck
              ref={milestoneModalFormRef}
              milestone={selectedMilestone}
              object={selectedObject}
            />
          ) : (
            <div>{t('workspace.selectObjectAndMilestone')}</div>
          )}
        </ModalCenter>
      ) : (
        milestoneFillModal
      )}
    </>
  )
}
